import { attachRailIndicator, revealInRail } from '@theme/rail-indicator';
import { standardEvents as standardEventsPromise } from '@theme/standard-events-bridge';
import { deferAnimationsToFirstPaint } from '@theme/card-enter';
import {
  IntentPrefetcher,
  finePointerQuery,
  isPlainNavigationClick,
  networkAllowsSpeculation,
} from '@theme/rail-navigation';
import { loadStylesheets as loadOwnerStylesheets, stylesheetsIn } from '@theme/section-styles';

(() => {
  'use strict';

  const ownerSelector = '[data-collection-owner]';
  const linkSelector = '[data-collection-link]';
  const responseCacheLimit = 6;
  const responseCacheLifetime = 30_000;
  const collectionTemplateCacheLifetime = 300_000;
  const collectionTemplateRefreshDelay = 1_000;
  const navigationSnapshotCacheLifetime = 120_000;
  const maxConcurrentPrefetches = 2;
  const maxAnimatedCards = 16;
  const compiledStylesheetPattern = '/compiled_assets/styles.css';
  const filterOriginParam = 'filter_origin';
  const defaultFilterOrigin = '/products';
  const executableScriptTypes = new Set([
    '',
    'module',
    'text/javascript',
    'application/javascript',
  ]);
  function filterSignature(url) {
    const entries = [];
    for (const [key, value] of url.searchParams.entries()) {
      if ((key.startsWith('filter.') || key === 'sort_by') && value) {
        entries.push(`${key}=${value}`);
      }
    }
    return entries.sort().join('&');
  }

  function productFiltersFromParams(searchParams) {
    const filters = [];
    const price = {};
    for (const [key, value] of searchParams.entries()) {
      if (!key.startsWith('filter.') || !value) continue;
      const segments = key.split('.');
      const scope = segments[1];
      const field = segments[2];
      if (scope === 'v' && field === 'availability') {
        filters.push({ available: value === '1' || value === 'true' });
      } else if (scope === 'v' && field === 'price') {
        const bound = Number(value);
        if (Number.isFinite(bound)) {
          if (segments[3] === 'gte') price.min = bound;
          if (segments[3] === 'lte') price.max = bound;
        }
      } else if (scope === 'v' && field === 'option' && segments[3]) {
        filters.push({ variantOption: { name: segments.slice(3).join('.'), value } });
      } else if (field === 'm' && segments[3] && segments[4]) {
        const metafield = {
          namespace: segments[3],
          key: segments.slice(4).join('.'),
          value,
        };
        filters.push(scope === 'p' ? { productMetafield: metafield } : { variantMetafield: metafield });
      } else if (scope === 'p' && field === 'product_type') {
        filters.push({ productType: value });
      } else if (scope === 'p' && field === 'vendor') {
        filters.push({ productVendor: value });
      } else if (scope === 'p' && field === 'tag') {
        filters.push({ tag: value });
      }
    }
    if ('min' in price || 'max' in price) filters.push({ price });
    return filters;
  }

  class CollectionComponent extends HTMLElement {
    connectedCallback() {
      if (this.redirectUnfilteredFilterOrigin()) return;
      this.reinitialize();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    get filtersInPlace() {
      return this.dataset.filterScope === 'current';
    }

    redirectUnfilteredFilterOrigin() {
      if (window.Shopify?.designMode) return false;

      const originUrl = this.unfilteredFilterOrigin(window.location.href);
      if (!originUrl) return false;

      window.location.replace(originUrl.href);
      return true;
    }

    unfilteredFilterOrigin(url) {
      const destination = new URL(url, window.location.href);
      const filterOrigin = destination.searchParams.get(filterOriginParam);
      const hasActiveFilters = Array.from(destination.searchParams.entries()).some(
        ([key, value]) => key.startsWith('filter.') && value
      );
      if (!filterOrigin || hasActiveFilters) return null;

      const originUrl = new URL(filterOrigin, window.location.origin);
      if (!this.isValidFilterOrigin(originUrl)) return null;

      return originUrl;
    }

    isValidFilterOrigin(url) {
      const destination = new URL(url, window.location.href);
      if (destination.origin !== window.location.origin) return false;

      const matchingLink = this.links.find((link) => {
        const linkUrl = new URL(link.href, window.location.href);
        return linkUrl.origin === destination.origin && linkUrl.pathname === destination.pathname;
      });
      if (matchingLink) return true;

      const routeRoot = window.Shopify?.routes?.root || '/';
      const rootPath = new URL(routeRoot, window.location.origin).pathname.replace(/\/?$/, '/');
      const productsPath = `${rootPath}products`;
      const collectionsPath = `${rootPath}collections/`;
      const collectionHandle = destination.pathname.startsWith(collectionsPath)
        ? destination.pathname.slice(collectionsPath.length).replace(/\/$/, '')
        : '';
      return destination.pathname === productsPath || Boolean(collectionHandle && !collectionHandle.includes('/'));
    }

    routeForUrl(url) {
      const destination = new URL(url, window.location.href);
      const matchingLink = this.links.find((link) => {
        const linkUrl = new URL(link.href, window.location.href);
        return linkUrl.pathname === destination.pathname;
      });
      const isProductsRoute = destination.pathname === defaultFilterOrigin;

      return {
        routeKey:
          matchingLink?.dataset.collectionRouteKey ||
          (isProductsRoute
            ? 'list-collections'
            : destination.pathname.split('/').filter(Boolean).at(-1)),
        targetView:
          matchingLink?.dataset.collectionTargetView ||
          (isProductsRoute ? 'list-collections' : 'collection'),
        title:
          matchingLink?.dataset.collectionTitle ||
          matchingLink?.textContent.trim() ||
          '',
      };
    }

    reinitialize() {
      const previousFilterIndexCacheKey = this.activeFilterIndexCacheKey || null;
      const preservedTemplateResponses = new Map();
      const preservedTemplateTimestamps = new Map();
      this.collectionTemplateCacheKeys?.forEach((cacheKey) => {
        const cachedAt = this.responseCacheTimestamps?.get(cacheKey) || 0;
        if (
          Date.now() - cachedAt > this.responseCacheLifetimeFor(cacheKey) ||
          this.prefetchControllers?.has(cacheKey) ||
          this.navigationCacheKey === cacheKey
        ) {
          return;
        }

        const cachedResponse = this.responseCache?.get(cacheKey);
        if (!cachedResponse) return;
        preservedTemplateResponses.set(cacheKey, cachedResponse);
        preservedTemplateTimestamps.set(cacheKey, cachedAt);
      });

      this.cleanup();

      this.lifecycleController = new AbortController();
      this.responseCache = preservedTemplateResponses;
      this.responseCacheTimestamps = preservedTemplateTimestamps;
      this.collectionTemplateCacheKeys = new Set(preservedTemplateResponses.keys());
      this.navigationSnapshotCacheKeys = new Set();
      this.prefetchControllers = new Map();
      this.collectionTemplatePreloadPromise = null;
      this.collectionTemplatePreloadPending = false;
      this.activeFilterIndexCacheKey =
        previousFilterIndexCacheKey &&
        preservedTemplateResponses.has(previousFilterIndexCacheKey)
          ? previousFilterIndexCacheKey
          : null;
      this.criticalPrefetchKey = null;
      this.pageSettled = document.readyState === 'complete';
      this.activeAnimations = new Set();
      this.intentId = 0;
      this.navigationController = null;
      this.lastCommittedPageUrl = window.location.href;
      this.reducedMotion = (window.DieuxFrame?.motionQuery ?? window.matchMedia('(prefers-reduced-motion: reduce)'));
      this.refreshReferences();
      this.loadFilterModule();
      if (this.changeViewButton && !this.actions?.hidden) {
        this.changeViewButton.disabled = false;
        this.changeViewButton.removeAttribute('aria-disabled');
      }

      const { signal } = this.lifecycleController;

      this.addEventListener('click', this.onClick, { signal });
      this.addEventListener('pointerover', this.onIndicatorIntent, { signal });
      this.addEventListener('pointerout', this.onIndicatorIntent, { signal });
      this.addEventListener('focusin', this.onFocusIn, { signal });
      this.addEventListener('focusout', this.onIndicatorIntent, { signal });
      this.addEventListener('click', this.onChangeViewClick, { signal });
      window.addEventListener('popstate', this.onPopState, { signal });
      window.addEventListener('load', this.onPrefetchReadinessChange, {
        signal,
        once: true,
      });
      document.addEventListener('visibilitychange', this.onPrefetchReadinessChange, {
        signal,
      });
      navigator.connection?.addEventListener?.('change', this.onPrefetchReadinessChange, {
        signal,
      });

      this.prefetcher = new IntentPrefetcher({
        root: this,
        linkSelector,
        canPrefetch: (link) => this.canPrefetch(link),
        prefetch: this.onPrefetchIntent,
      });
      this.prefetcher.connect();

      attachRailIndicator(this.tabsRail, () => this.arcTarget());

      document.fonts?.ready.then(() => {
        if (signal.aborted) return;
        this.scrollLinkIntoView(
          this.querySelector(`${linkSelector}[aria-current="page"]`),
          { behavior: 'auto', align: 'center' }
        );
      });

      if (
        this.hasAttribute('data-primary-collection') ||
        this.hasAttribute('data-filter-target-self')
      ) {
        const currentUrl = new URL(window.location.href);
        history.replaceState(
          {
            ...(history.state || {}),
            dieuxCollectionRouteKey: this.dataset.collectionRouteKey,
            dieuxCollectionView: this.dataset.collectionView,
            dieuxCollectionSectionId: this.dataset.sectionId,
            dieuxCollectionUrl: `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
          },
          '',
          window.location.href
        );
      }

      this.setState('ready');
      this.scheduleArc();
      this.scrollLinkIntoView(this.querySelector(`${linkSelector}[aria-current="page"]`), {
        behavior: 'auto',
        align: 'center',
      });
      if (this.pageSettled) this.preloadCollectionTemplates();
    }

    cleanup() {
      if (this.changeViewButton) {
        this.changeViewButton.disabled = true;
        this.changeViewButton.setAttribute('aria-disabled', 'true');
      }
      this.lifecycleController?.abort();
      this.navigationController?.abort();
      window.clearTimeout(this.collectionTemplateRefreshTimer);
      this.collectionTemplateRefreshTimer = undefined;
      if (this.navigationCacheKey) this.deleteCachedResponse(this.navigationCacheKey);
      this.prefetcher?.disconnect();
      this.prefetchControllers?.forEach((controller) => controller.abort());
      this.cancelAnimations();
      this.stopGlide();
      attachRailIndicator(this.tabsRail, null);
      this.prefetcher = null;
      this.navigationController = null;
      this.navigationCacheKey = null;
    }

    refreshReferences() {
      this.results = this.querySelector('[data-collection-ref="results"]');
      this.status = this.querySelector('[data-collection-status]');
      this.tabsList = this.querySelector('[data-collection-tabs-list]');
      this.tabsScroller = this.querySelector('[data-collection-tabs-scroller]');
      this.tabsRail = this.querySelector('[data-collection-tabs-rail]');
      this.changeViewButton = this.querySelector('[data-collection-change-view]');
      this.filterButton = this.querySelector('[data-collection-filter]');
      this.actions = this.querySelector('[data-collection-actions]');
      this.filterOwner = this.querySelector('[data-collection-filter-owner]');
    }

    get links() {
      return Array.from(this.querySelectorAll(linkSelector));
    }

    get cards() {
      if (!this.results) return [];

      const cards = Array.from(this.results.querySelectorAll('[data-collection-card], [data-product-list-promo-card]'));
      return cards.length ? cards : Array.from(this.results.children);
    }

    onClick = (event) => {
      const link = event.target.closest(linkSelector);
      if (!link?.hasAttribute('data-rail-nav-ajax') || !isPlainNavigationClick(event, link)) return;

      event.preventDefault();
      this.prefetcher?.cancel(link);
      this.activate(link, { historyMode: 'push', fallbackUrl: link.href, glideToResults: true });
    };

    onIndicatorIntent = () => {
      queueMicrotask(() => this.scheduleArc());
    };

    onFocusIn = (event) => {
      const link = event.target.closest(linkSelector);
      if (!link) return;

      this.scheduleArc();
      if (link.matches(':focus-visible')) this.scrollLinkIntoView(link, { behavior: 'auto' });
    };

    /**
     * A press is committed intent, so it skips the delay and the low priority
     * and enters this owner's own navigation lane: the request it starts is
     * normally still in flight when the click arrives, and `activate` adopts it.
     *
     * @param {HTMLAnchorElement} link - The link the intent landed on.
     * @param {{priority?: string}} options - Intent strength.
     * @returns {unknown} The in-flight request, when one was started.
     */
    onPrefetchIntent = (link, { priority } = {}) => {
      if (priority !== 'high') return this.prefetch(link);

      const cacheKey = this.responseRequest(link).url.href;
      if (this.responseCache.has(cacheKey)) return null;

      return this.readSection(link, 'navigation').catch(() => {});
    };

    onPrefetchReadinessChange = () => {
      this.pageSettled = document.readyState === 'complete';

      if (!networkAllowsSpeculation()) {
        window.clearTimeout(this.collectionTemplateRefreshTimer);
        this.collectionTemplateRefreshTimer = undefined;
        Array.from(this.prefetchControllers.keys()).forEach((cacheKey) => {
          this.deleteCachedResponse(cacheKey, { abortPrefetch: true });
        });
        return;
      }

      if (this.pageSettled) this.preloadCollectionTemplates();
    };

    onChangeViewClick = (event) => {
      if (!event.target.closest('[data-collection-change-view]')) return;

      const showAlternate = this.dataset.changeView !== 'alternate';
      this.dataset.changeView = showAlternate ? 'alternate' : 'primary';
      this.changeViewButton?.setAttribute('aria-pressed', String(showAlternate));
    };

    onPopState = (event) => {
      if (
        !this.hasAttribute('data-primary-collection') &&
        !this.hasAttribute('data-filter-target-self')
      ) {
        return;
      }

      const stateRouteKey = event.state?.dieuxCollectionRouteKey || null;
      const stateUrl = event.state?.dieuxCollectionUrl || null;
      const pathLink = this.links.find((link) => {
        const target = new URL(link.href, window.location.href);
        return target.pathname === window.location.pathname;
      });
      const link =
        this.links.find(
          (candidate) => candidate.dataset.collectionRouteKey === stateRouteKey
        ) || pathLink;
      const activeElement = document.activeElement;
      const identity = this.querySelector('[data-collection-ref="identity"]');
      const restoreFocus = Boolean(
        this.results?.contains(activeElement) || identity?.contains(activeElement)
      );
      const historicalUrl = new URL(window.location.href, window.location.origin).href;
      const filterOrigin = this.unfilteredFilterOrigin(historicalUrl);
      if (filterOrigin) {
        const originRoute = this.routeForUrl(filterOrigin);
        this.navigateToUrl(filterOrigin.href, {
          ...originRoute,
          historyMode: 'replace',
          restoreFocus,
          allowNavigationChange: true,
        });
        return;
      }

      const instantFilterDrawer = this.filterOwner?.querySelector('collection-filter-drawer');

      if (
        instantFilterDrawer?.applyInstantUrl?.(historicalUrl, {
          historyMode: 'none',
          restoreFocus,
        })
      ) {
        return;
      }

      if (!link && stateRouteKey && stateUrl) {
        this.navigateToUrl(historicalUrl, {
          routeKey: stateRouteKey,
          targetView: event.state?.dieuxCollectionView || 'collection',
          title: event.state?.dieuxCollectionTitle || '',
          historyMode: 'none',
          restoreFocus,
          allowNavigationChange: true,
        });
        return;
      }

      if (!link) {
        window.location.reload();
        return;
      }

      this.activate(link, {
        historyMode: 'none',
        fallbackUrl: historicalUrl,
        requestUrl: historicalUrl,
        forceRequest: true,
        restoreFocus,
      });
    };

    setState(state) {
      const wasArcLocked = this.isArcLocked();
      this.dataset.collectionState = state;
      const busy = state === 'loading' || state === 'outgoing' || state === 'incoming';
      const transitioning = state === 'outgoing' || state === 'incoming';

      this.toggleAttribute('data-collection-transitioning', transitioning);
      this.results?.setAttribute('aria-busy', String(busy));
      if (wasArcLocked && !this.isArcLocked()) this.scheduleArc();
    }

    isArcLocked() {
      return (
        this.dataset.collectionState === 'loading' ||
        this.dataset.collectionState === 'outgoing' ||
        this.dataset.collectionState === 'incoming'
      );
    }

    interactionArcTarget() {
      const hoveredLink = finePointerQuery.matches ? this.querySelector(`${linkSelector}:hover`) : null;
      if (hoveredLink) return hoveredLink;

      const focusedLink = document.activeElement?.closest?.(linkSelector);
      return focusedLink && this.contains(focusedLink) ? focusedLink : null;
    }

    announce(template, title = '') {
      if (!this.status) return;

      this.status.textContent = template ? template.replace('__TITLE__', () => title) : '';
    }

    setRequestedLink(requestedLink) {
      this.links.forEach((link) => {
        link.toggleAttribute('data-dieux-tabs-requested', link === requestedLink);
      });
      this.scheduleArc();
    }

    setActiveLink(routeKey) {
      let activeLink = null;

      this.links.forEach((link) => {
        const active = link.dataset.collectionRouteKey === routeKey;
        link.toggleAttribute('aria-current', active);
        if (active) link.setAttribute('aria-current', 'page');
        link.removeAttribute('data-dieux-tabs-requested');
        if (active) activeLink = link;
      });

      this.scheduleArc();
      return activeLink;
    }

    preloadCollectionTemplates() {
      if (this.collectionTemplatePreloadPromise) {
        this.collectionTemplatePreloadPending = true;
        return this.collectionTemplatePreloadPromise;
      }
      if (!this.canPreloadCollectionTemplates()) return Promise.resolve([]);

      this.collectionTemplatePreloadPending = false;
      this.cacheCurrentPageSnapshot();

      const run = (async () => {
        const links = Array.from(this.tabsScroller?.querySelectorAll(linkSelector) || []);
        const results = await Promise.all(
          links.map((link) =>
            this.prefetchUrl(link.href, {
              routeKey: link.dataset.collectionRouteKey,
              targetView: link.dataset.collectionTargetView,
              title: link.dataset.collectionTitle,
              allowSameRoute: true,
              collectionTemplate: true,
            })
          )
        );
        await this.loadFilterModule();
        if (!this.isConnected || !this.canPreloadCollectionTemplates()) return results;

        const filterDrawer = this.filterOwner?.querySelector('collection-filter-drawer');
        if (typeof filterDrawer?.preloadFilterIndex === 'function') {
          results.push(
            await filterDrawer.preloadFilterIndex({
              collectionTemplate: true,
              priority: 'high',
            })
          );
        }
        return results;
      })();

      this.collectionTemplatePreloadPromise = run;
      const settle = () => {
        if (this.collectionTemplatePreloadPromise !== run) return;

        this.collectionTemplatePreloadPromise = null;
        if (!this.collectionTemplatePreloadPending) {
          this.scheduleCollectionTemplateRefresh();
          return;
        }

        this.collectionTemplatePreloadPending = false;
        this.preloadCollectionTemplates();
      };
      run.then(settle, settle);
      return run;
    }

    scheduleCollectionTemplateRefresh() {
      window.clearTimeout(this.collectionTemplateRefreshTimer);
      this.collectionTemplateRefreshTimer = undefined;
      if (!this.canPreloadCollectionTemplates()) return;

      this.collectionTemplateRefreshTimer = window.setTimeout(() => {
        this.collectionTemplateRefreshTimer = undefined;
        this.preloadCollectionTemplates();
      }, collectionTemplateCacheLifetime + collectionTemplateRefreshDelay);
    }

    canPreloadCollectionTemplates() {
      return (
        this.isConnected &&
        this.pageSettled &&
        Boolean(this.tabsScroller) &&
        !this.hasAttribute('data-collection-prefetch-disabled') &&
        networkAllowsSpeculation()
      );
    }

    canRequest(link) {
      if (!link || !link.hasAttribute('data-rail-nav-ajax') || !link.dataset.collectionRouteKey) {
        return false;
      }

      const target = new URL(link.href, window.location.href);
      const targetsCurrentRoute =
        link.dataset.collectionRouteKey === this.dataset.collectionRouteKey;
      const isSameRoutePrefetch =
        link.hasAttribute('data-collection-prefetch-same-route') &&
        target.href !== window.location.href;

      return (
        target.origin === window.location.origin &&
        (!targetsCurrentRoute || isSameRoutePrefetch)
      );
    }

    canPrefetch(link, { allowBeforePageSettled = false } = {}) {
      return (
        this.isConnected &&
        (this.pageSettled || allowBeforePageSettled) &&
        !this.hasAttribute('data-collection-prefetch-disabled') &&
        networkAllowsSpeculation() &&
        this.canRequest(link)
      );
    }

    prefetch(link, { priority = 'low' } = {}) {
      const isCritical = priority === 'high';
      if (!this.canPrefetch(link, { allowBeforePageSettled: isCritical })) {
        return Promise.resolve(null);
      }

      const cacheKey = this.responseRequest(link).url.href;
      const isTemplatePreload = link.hasAttribute('data-collection-template-preload');
      if (
        !isCritical &&
        !isTemplatePreload &&
        this.criticalPrefetchKey &&
        this.criticalPrefetchKey !== cacheKey
      ) {
        return Promise.resolve(null);
      }
      if (
        !isCritical &&
        !isTemplatePreload &&
        this.prefetchControllers.size >= maxConcurrentPrefetches &&
        !this.responseCache.has(cacheKey)
      ) {
        return Promise.resolve(null);
      }

      link.dataset.collectionPrefetchState = 'prefetching';
      return this.readSection(link, isCritical ? 'critical-prefetch' : 'prefetch')
        .then((data) => {
          if (!data || !this.isConnected) return data;
          link.dataset.collectionPrefetchState = 'prefetched';
          return data;
        })
        .catch(() => {
          if (this.isConnected) link.dataset.collectionPrefetchState = 'failed';
          return null;
        });
    }

    prefetchUrl(
      url,
      options = {}
    ) {
      const link = this.createPrefetchLink(url, options);
      if (!link) return Promise.resolve(null);

      return this.prefetch(link, { priority: options.priority });
    }

    createPrefetchLink(
      url,
      {
        routeKey,
        targetView = 'collection',
        title = '',
        allowNavigationChange = false,
        allowSameRoute = false,
        collectionTemplate = false,
      } = {}
    ) {
      if (!routeKey) return null;

      const link = document.createElement('a');
      link.href = new URL(url, window.location.href).href;
      link.dataset.railNavAjax = '';
      link.dataset.collectionRouteKey = routeKey;
      link.dataset.collectionTargetView = targetView;
      link.dataset.collectionTitle = title;
      link.toggleAttribute('data-collection-allow-navigation-change', allowNavigationChange);
      link.toggleAttribute('data-collection-prefetch-same-route', allowSameRoute);
      link.toggleAttribute('data-collection-template-preload', collectionTemplate);
      return link;
    }

    prioritizePrefetchUrl(url, options = {}) {
      const link = this.createPrefetchLink(url, {
        ...options,
        collectionTemplate: true,
      });
      if (
        !link ||
        !this.canPrefetch(link, { allowBeforePageSettled: true })
      ) {
        return Promise.resolve(null);
      }

      const cacheKey = this.responseRequest(link).url.href;
      const previousFilterIndexCacheKey = this.activeFilterIndexCacheKey;
      if (
        previousFilterIndexCacheKey &&
        previousFilterIndexCacheKey !== cacheKey
      ) {
        this.deleteCachedResponse(previousFilterIndexCacheKey, {
          abortPrefetch: true,
        });
      }
      this.activeFilterIndexCacheKey = cacheKey;

      const cachedResponse = this.readCachedResponse(cacheKey);
      if (cachedResponse && !this.prefetchControllers.has(cacheKey)) {
        return cachedResponse;
      }

      this.criticalPrefetchKey = cacheKey;
      Array.from(this.prefetchControllers.keys()).forEach((pendingCacheKey) => {
        if (
          pendingCacheKey === cacheKey ||
          this.collectionTemplateCacheKeys.has(pendingCacheKey)
        ) {
          return;
        }
        this.deleteCachedResponse(pendingCacheKey, { abortPrefetch: true });
      });

      const request = cachedResponse || this.prefetch(link, { priority: 'high' });
      return Promise.resolve(request).finally(() => {
        if (this.criticalPrefetchKey !== cacheKey) return;

        this.criticalPrefetchKey = null;
        if (
          !this.isConnected ||
          !this.collectionTemplatePreloadPending
        ) {
          return;
        }

        queueMicrotask(() => {
          if (this.isConnected) this.preloadCollectionTemplates();
        });
      });
    }

    responseRequest(link, requestedUrl = null) {
      const url = new URL(requestedUrl || link.href, window.location.href);
      url.hash = '';
      const targetView = link.dataset.collectionTargetView || 'collection';
      const targetRouteKey = link.dataset.collectionRouteKey || '';
      const crossTemplate =
        link.hasAttribute('data-collection-template-preload') ||
        targetView !== this.dataset.collectionView ||
        (Boolean(targetRouteKey) && targetRouteKey !== this.dataset.collectionRouteKey);

      if (!crossTemplate) url.searchParams.set('section_id', this.dataset.sectionId);

      return { url, crossTemplate };
    }

    collectionTemplateCacheKey(link, requestedUrl = null) {
      const url = new URL(requestedUrl || link.href, window.location.href);
      url.hash = '';
      url.searchParams.delete('section_id');
      return url.href;
    }

    responseCacheLifetimeFor(cacheKey) {
      if (cacheKey === this.activeFilterIndexCacheKey) {
        return Number.POSITIVE_INFINITY;
      }

      if (this.collectionTemplateCacheKeys?.has(cacheKey)) {
        return collectionTemplateCacheLifetime;
      }

      if (this.navigationSnapshotCacheKeys?.has(cacheKey)) {
        return navigationSnapshotCacheLifetime;
      }

      return responseCacheLifetime;
    }

    cacheCurrentPageSnapshot() {
      const header = this.querySelector('.g-collection__header');
      const identity = this.querySelector('[data-collection-ref="identity"]');
      const results = this.results;
      if (!header || !identity || !results) return;

      const pageUrl = new URL(this.lastCommittedPageUrl || window.location.href);
      pageUrl.hash = '';
      const cacheKey = pageUrl.href;
      const identityRow = identity.closest('.g-collection__identity-row');
      const filterOwner = this.filterOwner;
      const snapshotHeader = header.cloneNode(true);
      const snapshotFilterOwner = filterOwner?.cloneNode(true) || null;
      const snapshotFilterControl = snapshotHeader.querySelector(
        '[data-collection-filter-control]'
      );
      const activeFilterCount = Array.from(pageUrl.searchParams.entries()).filter(
        ([key, value]) => key.startsWith('filter.') && value
      ).length;
      const snapshotTrigger = snapshotFilterControl?.querySelector(
        '[data-collection-filter]'
      );
      const snapshotTriggerLabel =
        snapshotTrigger?.dataset.collectionFilterLabel ||
        snapshotTrigger?.textContent.trim().replace(/\s+\(\d+\)$/, '');
      if (snapshotTrigger && snapshotTriggerLabel) {
        snapshotTrigger.textContent = `${snapshotTriggerLabel} (${activeFilterCount})`;
      }
      const snapshotClearButton = snapshotFilterControl?.querySelector(
        '[data-collection-filter-clear]'
      );
      if (snapshotClearButton) snapshotClearButton.hidden = activeFilterCount === 0;
      snapshotHeader.classList.toggle(
        'g-collection__header--filtered',
        !this.filtersInPlace && activeFilterCount > 0
      );
      snapshotFilterOwner
        ?.querySelectorAll('[data-collection-ref="filter-form"] input[type="checkbox"]')
        .forEach((input) => {
          input.checked = pageUrl.searchParams.getAll(input.name).includes(input.value);
          if (activeFilterCount === 0) input.disabled = false;
        });
      const sectionWrapper = this.closest('[id^="shopify-section-"]');
      const dynamicStyles = sectionWrapper
        ? Array.from(sectionWrapper.querySelectorAll('style'))
            .map((style) => style.textContent)
            .filter((css) => css.includes(`color-custom-${this.dataset.sectionId}`))
        : [];
      const data = {
        header: snapshotHeader,
        identity: identity.cloneNode(true),
        identityInHeader: header.contains(identity),
        identityRow: identityRow?.cloneNode(true) || null,
        results: results.cloneNode(true),
        crossTemplate: true,
        className: this.className,
        sectionId: this.dataset.sectionId,
        view: this.dataset.collectionView,
        routeKey: this.dataset.collectionRouteKey,
        handle: this.dataset.activeHandle,
        title: this.dataset.activeTitle,
        pageTitle: this.dataset.pageTitle,
        pageTemplate: this.dataset.pageTemplate,
        documentTitle: this.dataset.documentTitle,
        canonicalUrl: this.dataset.canonicalUrl,
        ogDescription: this.dataset.ogDescription,
        hasPageDescription: this.hasAttribute('data-has-page-description'),
        pageDescription: this.dataset.pageDescription || '',
        pageImageUrl: this.dataset.pageImageUrl || '',
        pageImageWidth: this.dataset.pageImageWidth || '',
        pageImageHeight: this.dataset.pageImageHeight || '',
        viewEventPayload: this.getAttribute('view-event-payload') || '',
        filterControl: snapshotFilterControl?.cloneNode(true) || null,
        filterButtonControls: this.filterButton?.getAttribute('aria-controls') || '',
        filterOwner: snapshotFilterOwner,
        isPrimaryCollection: this.hasAttribute('data-primary-collection'),
        filtersTargetSelf: this.hasAttribute('data-filter-target-self'),
        actionsHidden: !this.actions || this.actions.hidden,
        productListScript: this.dataset.productListScript || '',
        stylesheetHrefs: [],
        dynamicStyles,
        sections: this.captureMainSections(
          document.getElementById('MainContent'),
          sectionWrapper
        ),
      };

      this.responseCache.set(cacheKey, Promise.resolve(data));
      this.responseCacheTimestamps.set(cacheKey, Date.now());
      this.navigationSnapshotCacheKeys.add(cacheKey);
      const hasContentQuery = Array.from(pageUrl.searchParams.entries()).some(
        ([key, value]) =>
          Boolean(value) &&
          (
            key.startsWith('filter.') ||
            ['page', 'sort', 'sort_by', 'order', 'in_stock'].includes(key)
          )
      );
      const currentTemplateLink = this.links.find((link) => {
        const linkUrl = new URL(link.href, window.location.href);
        return (
          link.dataset.collectionRouteKey === this.dataset.collectionRouteKey &&
          linkUrl.pathname === pageUrl.pathname
        );
      });
      if (currentTemplateLink && !hasContentQuery) {
        const templateCacheKey = this.collectionTemplateCacheKey(currentTemplateLink);
        this.responseCache.set(templateCacheKey, Promise.resolve(data));
        this.responseCacheTimestamps.set(templateCacheKey, Date.now());
        this.collectionTemplateCacheKeys.add(templateCacheKey);
      }
      this.trimResponseCache(cacheKey);
    }

    navigateToUrl(
      url,
      {
        routeKey,
        targetView = 'collection',
        title = '',
        fallbackUrl = url,
        historyMode = 'push',
        restoreFocus = false,
        preserveFilterDialog = false,
        allowNavigationChange = false,
        prepareData = null,
        requirePreparedCommit = false,
        resultsOnly = false,
        glideToResults = false,
        silent = false,
      } = {}
    ) {
      const destination = new URL(url, window.location.href);
      const link = document.createElement('a');
      link.href = destination.href;
      link.dataset.collectionRouteKey = routeKey || this.dataset.collectionRouteKey;
      link.dataset.collectionTargetView = targetView;
      link.dataset.collectionTitle = title;
      link.toggleAttribute('data-collection-allow-navigation-change', allowNavigationChange);

      return this.activate(link, {
        historyMode,
        fallbackUrl,
        requestUrl: destination.href,
        forceRequest: true,
        restoreFocus,
        preserveFilterDialog,
        prepareData,
        requirePreparedCommit,
        resultsOnly,
        glideToResults,
        silent,
      });
    }

    commitInstantFilterUrl(
      url,
      {
        historyMode = 'push',
        previousPageUrl = this.lastCommittedPageUrl || document.referrer || '',
      } = {}
    ) {
      const committedUrl = new URL(url, window.location.href);
      if (committedUrl.origin !== window.location.origin) return false;

      const path = `${committedUrl.pathname}${committedUrl.search}${committedUrl.hash}`;
      const state = {
        ...(history.state || {}),
        dieuxCollectionRouteKey: this.dataset.collectionRouteKey,
        dieuxCollectionView: this.dataset.collectionView,
        dieuxCollectionSectionId: this.dataset.sectionId,
        dieuxCollectionUrl: path,
        dieuxCollectionTitle: this.dataset.activeTitle || '',
      };

      if (historyMode === 'push') {
        history.pushState(state, '', path);
      } else if (historyMode === 'replace') {
        history.replaceState(state, '', path);
      }

      this.lastCommittedPageUrl = committedUrl.href;
      const data = {
        pageTemplate: this.dataset.pageTemplate,
        documentTitle: this.dataset.documentTitle,
        viewEventPayload: this.getAttribute('view-event-payload') || '',
      };
      const navigation = { committedUrl, previousPageUrl };
      this.dispatchVirtualPageView(data, navigation);
      this.dispatchStandardViews(data, navigation);
      return true;
    }

    readCachedResponse(cacheKey) {
      if (!cacheKey || !this.responseCache.has(cacheKey)) return null;
      if (this.prefetchControllers.has(cacheKey)) {
        return this.responseCache.get(cacheKey);
      }

      const cachedAt = this.responseCacheTimestamps.get(cacheKey) || 0;
      if (Date.now() - cachedAt <= this.responseCacheLifetimeFor(cacheKey)) {
        const cachedResponse = this.responseCache.get(cacheKey);
        this.responseCache.delete(cacheKey);
        this.responseCache.set(cacheKey, cachedResponse);
        return cachedResponse;
      }

      this.deleteCachedResponse(cacheKey, { abortPrefetch: true });
      return null;
    }

    readSection(link, requestKind, requestedUrl = null) {
      const templateCacheKey = this.collectionTemplateCacheKey(link, requestedUrl);
      const isCollectionTemplatePreload = link.hasAttribute(
        'data-collection-template-preload'
      );
      if (this.collectionTemplateCacheKeys.has(templateCacheKey)) {
        const cachedTemplate = this.readCachedResponse(templateCacheKey);
        if (cachedTemplate) return cachedTemplate;
      }
      if (isCollectionTemplatePreload) {
        this.collectionTemplateCacheKeys.add(templateCacheKey);
      }

      const responseRequest = this.responseRequest(link, requestedUrl);
      const requestUrl = responseRequest.url;
      const cacheKey = requestUrl.href;
      const cachedResponse = this.readCachedResponse(cacheKey);
      if (cachedResponse) return cachedResponse;

      const controller = new AbortController();
      if (requestKind !== 'navigation') {
        this.prefetchControllers.set(cacheKey, controller);
      } else {
        this.navigationController?.abort();
        if (this.navigationCacheKey) this.responseCache.delete(this.navigationCacheKey);
        this.navigationController = controller;
        this.navigationCacheKey = cacheKey;
      }

      const request = fetch(requestUrl, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
        priority: requestKind === 'prefetch' ? 'low' : 'high',
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Collection section request failed: ${response.status}`);
          }

          return response.text();
        })
        .then((html) => this.parseSection(html, link, responseRequest.crossTemplate))
        .then((data) => {
          this.responseCacheTimestamps.set(cacheKey, Date.now());
          return data;
        })
        .catch((error) => {
          if (this.responseCache.get(cacheKey) === request) this.deleteCachedResponse(cacheKey);
          throw error;
        })
        .finally(() => {
          if (this.prefetchControllers.get(cacheKey) === controller) {
            this.prefetchControllers.delete(cacheKey);
          }
          if (this.navigationController === controller) {
            this.navigationController = null;
            this.navigationCacheKey = null;
          }
        });

      this.responseCache.set(cacheKey, request);
      this.responseCacheTimestamps.set(cacheKey, Date.now());
      this.trimResponseCache(cacheKey);
      return request;
    }

    deleteCachedResponse(cacheKey, { abortPrefetch = false } = {}) {
      if (abortPrefetch) this.prefetchControllers.get(cacheKey)?.abort();
      this.prefetchControllers.delete(cacheKey);
      this.responseCache.delete(cacheKey);
      this.responseCacheTimestamps.delete(cacheKey);
      this.collectionTemplateCacheKeys?.delete(cacheKey);
      this.navigationSnapshotCacheKeys?.delete(cacheKey);
      if (this.activeFilterIndexCacheKey === cacheKey) {
        this.activeFilterIndexCacheKey = null;
      }
    }

    trimResponseCache(preservedKey) {
      for (const cacheKey of this.responseCache.keys()) {
        if (this.responseCache.size <= responseCacheLimit) return;
        const cachedAt = this.responseCacheTimestamps.get(cacheKey) || 0;
        const keepsActiveFilterIndex =
          cacheKey === this.activeFilterIndexCacheKey;
        const keepsFreshCollectionTemplate =
          this.collectionTemplateCacheKeys?.has(cacheKey) &&
          Date.now() - cachedAt <= collectionTemplateCacheLifetime;
        const keepsFreshNavigationSnapshot =
          this.navigationSnapshotCacheKeys?.has(cacheKey) &&
          Date.now() - cachedAt <= navigationSnapshotCacheLifetime;
        if (
          cacheKey === preservedKey ||
          cacheKey === this.navigationCacheKey ||
          keepsActiveFilterIndex ||
          keepsFreshCollectionTemplate ||
          keepsFreshNavigationSnapshot
        ) {
          continue;
        }
        this.deleteCachedResponse(cacheKey, { abortPrefetch: true });
      }
    }

    parseSection(html, link, crossTemplate) {
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const requestedRouteKey = link.dataset.collectionRouteKey;
      const requestedView = link.dataset.collectionTargetView || 'collection';
      const candidates = Array.from(parsedDocument.querySelectorAll(ownerSelector)).filter(
        (candidate) => {
          if (candidate.dataset.collectionRouteKey !== requestedRouteKey) return false;
          if (candidate.dataset.collectionView !== requestedView) return false;
          return crossTemplate || candidate.dataset.sectionId === this.dataset.sectionId;
        }
      );
      const nextRoot =
        candidates.find(
          (candidate) => candidate.dataset.sectionId === this.dataset.sectionId
        ) || candidates[0];
      const nextHeader = nextRoot?.querySelector('.g-collection__header');
      const nextIdentity = nextRoot?.querySelector('[data-collection-ref="identity"]');
      const nextIdentityRow = nextIdentity?.closest('.g-collection__identity-row');
      const nextResults = nextRoot?.querySelector('[data-collection-ref="results"]');
      const nextActions = nextRoot?.querySelector('[data-collection-actions]');
      const nextFilterOwner = nextRoot?.querySelector('[data-collection-filter-owner]');
      const nextFilterControl = nextRoot?.querySelector('[data-collection-filter-control]');
      const currentNavigationSignature = this.navigationSignature(this);
      const nextNavigationSignature = this.navigationSignature(nextRoot);

      if (
        !nextRoot ||
        !nextHeader ||
        !nextIdentity ||
        !nextResults ||
        (
          currentNavigationSignature !== nextNavigationSignature &&
          !link.hasAttribute('data-collection-allow-navigation-change')
        )
      ) {
        throw new Error('Collection section response did not match the requested owner.');
      }

      const sectionWrapper = nextRoot.closest('[id^="shopify-section-"]');
      const stylesheetHrefs = crossTemplate
        ? Array.from(parsedDocument.querySelectorAll('link[rel="stylesheet"]'))
            .map((stylesheet) => stylesheet.getAttribute('href') || '')
            .filter((href) => href.includes(compiledStylesheetPattern))
        : [];
      const dynamicStyles = sectionWrapper
        ? Array.from(sectionWrapper.querySelectorAll('style'))
            .map((style) => style.textContent)
            .filter((css) => css.includes(`color-custom-${nextRoot.dataset.sectionId}`))
        : [];
      const sections = this.captureMainSections(
        parsedDocument.getElementById('MainContent'),
        sectionWrapper
      );

      return {
        header: nextHeader.cloneNode(true),
        identity: nextIdentity.cloneNode(true),
        identityInHeader: nextHeader.contains(nextIdentity),
        identityRow: nextIdentityRow?.cloneNode(true) || null,
        results: nextResults.cloneNode(true),
        crossTemplate,
        className: nextRoot.className,
        sectionId: nextRoot.dataset.sectionId,
        view: nextRoot.dataset.collectionView,
        routeKey: nextRoot.dataset.collectionRouteKey,
        handle: nextRoot.dataset.activeHandle,
        title: nextRoot.dataset.activeTitle,
        pageTitle: nextRoot.dataset.pageTitle,
        pageTemplate: nextRoot.dataset.pageTemplate,
        documentTitle: nextRoot.dataset.documentTitle,
        canonicalUrl: nextRoot.dataset.canonicalUrl,
        ogDescription: nextRoot.dataset.ogDescription,
        hasPageDescription: nextRoot.hasAttribute('data-has-page-description'),
        pageDescription: nextRoot.dataset.pageDescription || '',
        pageImageUrl: nextRoot.dataset.pageImageUrl || '',
        pageImageWidth: nextRoot.dataset.pageImageWidth || '',
        pageImageHeight: nextRoot.dataset.pageImageHeight || '',
        viewEventPayload: nextRoot.getAttribute('view-event-payload') || '',
        filterControl: nextFilterControl?.cloneNode(true) || null,
        filterButtonControls: nextRoot.querySelector('[data-collection-filter]')?.getAttribute('aria-controls') || '',
        filterOwner: nextFilterOwner?.cloneNode(true) || null,
        isPrimaryCollection: nextRoot.hasAttribute('data-primary-collection'),
        filtersTargetSelf: nextRoot.hasAttribute('data-filter-target-self'),
        actionsHidden: !nextActions || nextActions.hidden,
        productListScript: nextRoot.dataset.productListScript || '',
        stylesheetHrefs,
        ownerStylesheetHrefs: stylesheetsIn(parsedDocument),
        dynamicStyles,
        sections,
      };
    }

    cloneResponseData(data) {
      return {
        ...data,
        header: data.header.cloneNode(true),
        identity: data.identity.cloneNode(true),
        identityRow: data.identityRow?.cloneNode(true) || null,
        results: data.results.cloneNode(true),
        filterControl: data.filterControl?.cloneNode(true) || null,
        filterOwner: data.filterOwner?.cloneNode(true) || null,
        stylesheetHrefs: [...data.stylesheetHrefs],
        dynamicStyles: [...data.dynamicStyles],
        sections: data.sections
          ? {
              before: data.sections.before.map((section) => section.cloneNode(true)),
              after: data.sections.after.map((section) => section.cloneNode(true)),
            }
          : null,
      };
    }

    loadFilterModule() {
      const source = this.filterOwner?.dataset.filterModuleSrc;
      if (!source || customElements.get('collection-filter-drawer')) return Promise.resolve();

      this.filterModulePromise ||= import(source).catch(() => null);
      return this.filterModulePromise;
    }

    updateFilterForm(currentForm, nextForm, { preserveChecked = false } = {}) {
      const nextInputs = new Map(
        Array.from(nextForm.querySelectorAll('input[type="checkbox"]')).map((input) => [
          `${input.name}\u0000${input.value}`,
          input,
        ])
      );

      currentForm.action = nextForm.action;
      currentForm.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        const nextInput = nextInputs.get(`${input.name}\u0000${input.value}`);
        if (!preserveChecked) input.checked = Boolean(nextInput?.checked);
        input.disabled = !nextInput || nextInput.disabled;
      });
    }

    navigationSignature(root) {
      if (!root) return '';

      return Array.from(root.querySelectorAll(linkSelector))
        .map((link) => {
          const target = new URL(link.getAttribute('href') || link.href, window.location.origin);
          return `${link.dataset.collectionRouteKey || ''}:${target.pathname}`;
        })
        .join('|');
    }

    loadStylesheets(data) {
      if (!data.crossTemplate) return Promise.resolve();

      const loads = data.stylesheetHrefs.map((href) => {
        const absoluteHref = new URL(href, window.location.href).href;
        const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find(
          (stylesheet) => stylesheet.href === absoluteHref
        );
        if (existing) return Promise.resolve();

        return new Promise((resolve, reject) => {
          const stylesheet = document.createElement('link');
          const timeout = window.setTimeout(() => {
            stylesheet.remove();
            reject(new Error('Collection destination stylesheet timed out.'));
          }, 8000);
          const settle = (callback) => {
            window.clearTimeout(timeout);
            callback();
          };
          stylesheet.rel = 'stylesheet';
          stylesheet.href = absoluteHref;
          stylesheet.dataset.dieuxCollectionStylesheet = 'true';
          stylesheet.addEventListener('load', () => settle(resolve), { once: true });
          stylesheet.addEventListener(
            'error',
            () =>
              settle(() =>
                reject(new Error('Collection destination stylesheet failed to load.'))
              ),
            { once: true }
          );
          document.head.append(stylesheet);
        });
      });

      return Promise.all(loads);
    }

    applyDynamicStyles(data) {
      document
        .querySelectorAll('style[data-g-collection-dynamic-style]')
        .forEach((style) => style.remove());

      data.dynamicStyles.forEach((css) => {
        const style = document.createElement('style');
        style.dataset.dieuxCollectionDynamicStyle = 'true';
        style.textContent = css;
        document.head.append(style);
      });
    }

    /**
     * The sections beside this owner read the collection through dynamic sources, so they belong to the destination as much as the grid does.
     *
     * @param {HTMLElement|null} main - The main content region to read.
     * @param {HTMLElement|null} ownerWrapper - This owner's section wrapper within it.
     * @returns {{before: HTMLElement[], after: HTMLElement[]}|null} Null when the
     *   response carried this section alone rather than a whole document.
     */
    captureMainSections(main, ownerWrapper) {
      if (!main || !ownerWrapper || ownerWrapper.parentElement !== main) return null;

      const wrappers = Array.from(main.children).filter((child) =>
        child.id.startsWith('shopify-section-')
      );
      const ownerIndex = wrappers.indexOf(ownerWrapper);
      if (ownerIndex === -1) return null;

      return {
        before: wrappers.slice(0, ownerIndex).map((wrapper) => wrapper.cloneNode(true)),
        after: wrappers.slice(ownerIndex + 1).map((wrapper) => wrapper.cloneNode(true)),
      };
    }

    commitSections(data) {
      const main = document.getElementById('MainContent');
      const ownerWrapper = this.closest('[id^="shopify-section-"]');
      if (!data.sections || !main || !ownerWrapper || ownerWrapper.parentElement !== main) {
        return;
      }

      const liveSections = new Map();
      Array.from(main.children).forEach((child) => {
        if (child === ownerWrapper || !child.id.startsWith('shopify-section-')) return;
        liveSections.set(child.id, child);
      });

      const adopted = [];
      const resolve = (source) => {
        if (!source.id || source.id === ownerWrapper.id) return null;

        const live = liveSections.get(source.id);
        liveSections.delete(source.id);
        if (live && live.outerHTML === source.outerHTML) return live;

        live?.remove();
        const replacement = source.cloneNode(true);
        adopted.push(replacement);
        return replacement;
      };

      const before = data.sections.before.map(resolve).filter(Boolean);
      const after = data.sections.after.map(resolve).filter(Boolean);

      let anchor = ownerWrapper;
      after.forEach((section) => {
        if (section.previousElementSibling !== anchor) anchor.after(section);
        anchor = section;
      });

      anchor = ownerWrapper;
      for (let index = before.length - 1; index >= 0; index -= 1) {
        const section = before[index];
        if (section.nextElementSibling !== anchor) anchor.before(section);
        anchor = section;
      }

      liveSections.forEach((section) => section.remove());
      adopted.forEach((section) => this.activateSectionScripts(section));
    }

    activateSectionScripts(section) {
      section.querySelectorAll('script').forEach((script) => {
        if (!executableScriptTypes.has((script.getAttribute('type') || '').toLowerCase())) {
          return;
        }

        const source = script.getAttribute('src');
        if (source) {
          const absoluteSource = new URL(source, window.location.href).href;
          const alreadyLoaded = Array.from(document.scripts).some(
            (candidate) => candidate.src === absoluteSource && !section.contains(candidate)
          );
          if (alreadyLoaded) {
            script.remove();
            return;
          }
        }

        const replacement = document.createElement('script');
        Array.from(script.attributes).forEach((attribute) => {
          replacement.setAttribute(attribute.name, attribute.value);
        });
        replacement.textContent = script.textContent;
        script.replaceWith(replacement);
      });
    }

    loadProductListScript(src) {
      if (!src || customElements.get('product-list')) return;
      const absoluteSrc = new URL(src, window.location.href).href;
      if (Array.from(document.scripts).some((script) => script.src === absoluteSrc)) return;

      const script = document.createElement('script');
      script.src = absoluteSrc;
      script.defer = true;
      script.fetchPriority = 'low';
      document.head.append(script);
    }

    animate(direction, cards = this.cards, { landing = null } = {}) {
      const bandTop = landing === null ? 0 : Math.min(0, landing - window.scrollY);
      const visibleCards = cards.filter((card) => {
        const bounds = card.getBoundingClientRect();
        return bounds.bottom > bandTop && bounds.top < window.innerHeight;
      });
      const targets = (visibleCards.length ? visibleCards : cards).slice(0, maxAnimatedCards);
      if (
        !targets.length ||
        this.reducedMotion.matches ||
        typeof targets[0].animate !== 'function'
      ) {
        return Promise.resolve([]);
      }

      const entering = direction === 'in';
      const keyframes = entering
        ? [
            { opacity: 0, transform: 'translateY(16px) scale(0.988)' },
            { opacity: 1, transform: 'translateY(0) scale(1)' },
          ]
        : [
            { opacity: 1, transform: 'translateY(0) scale(1)' },
            { opacity: 0, transform: 'translateY(-12px) scale(0.99)' },
          ];
      const duration = entering ? 220 : 130;
      const stagger = entering ? 20 : 8;
      const easing = entering ? 'cubic-bezier(.32, .06, .22, 1)' : 'cubic-bezier(.4, 0, 1, 1)';
      const animations = targets.map((target, index) => {
        const animation = target.animate(keyframes, {
          duration,
          delay: Math.min(index, 7) * stagger,
          easing,
          fill: 'both',
        });
        this.activeAnimations.add(animation);
        return animation;
      });

      if (entering) deferAnimationsToFirstPaint(animations);

      return Promise.all(animations.map((animation) => animation.finished.catch(() => {}))).then(
        () => animations
      );
    }

    clearAnimations(animations = []) {
      animations.forEach((animation) => {
        this.activeAnimations.delete(animation);
        animation.cancel();
      });
    }

    cancelAnimations() {
      this.activeAnimations?.forEach((animation) => animation.cancel());
      this.activeAnimations?.clear();
    }

    async transitionInstantResults(commit, { glideToResults = false } = {}) {
      if (typeof commit !== 'function') return false;

      const intentId = ++this.intentId;
      this.cancelAnimations();
      this.stopGlide();
      let outgoingAnimations = [];
      let incomingAnimations = [];

      try {
        this.setState('outgoing');
        const glide = glideToResults ? await this.glideToResults() : null;
        if (intentId !== this.intentId || !this.isConnected) return false;
        outgoingAnimations = await this.animate(
          'out',
          this.cards.filter((card) => !card.hidden),
          { landing: glide?.target ?? null }
        );
        if (intentId !== this.intentId || !this.isConnected) {
          this.clearAnimations(outgoingAnimations);
          return false;
        }

        commit();

        this.setState('incoming');
        const incomingPromise = this.animate(
          'in',
          this.cards.filter((card) => !card.hidden),
          { landing: glide?.target ?? null }
        );
        this.clearAnimations(outgoingAnimations);
        outgoingAnimations = [];

        incomingAnimations = await incomingPromise;
        this.clearAnimations(incomingAnimations);
        incomingAnimations = [];
        if (intentId !== this.intentId || !this.isConnected) return false;

        this.setState('ready');
        return true;
      } catch {
        this.clearAnimations(outgoingAnimations);
        this.clearAnimations(incomingAnimations);
        if (intentId === this.intentId && this.isConnected) this.setState('ready');
        return false;
      }
    }

    cancelActiveNavigation() {
      this.intentId += 1;
      this.navigationController?.abort();
      if (this.navigationCacheKey) this.deleteCachedResponse(this.navigationCacheKey);
      this.navigationController = null;
      this.navigationCacheKey = null;
      this.cancelAnimations();
      this.setRequestedLink(null);
      this.setState('ready');
      this.announce('');
    }

    async activate(
      link,
      {
        historyMode,
        fallbackUrl,
        requestUrl = null,
        forceRequest = false,
        restoreFocus = false,
        preserveFilterDialog = false,
        prepareData = null,
        requirePreparedCommit = false,
        resultsOnly = false,
        glideToResults = false,
        silent = false,
      }
    ) {
      const requestedRouteKey = link.dataset.collectionRouteKey;
      const requestedTitle = link.dataset.collectionTitle || link.textContent.trim();
      const requestedCacheKey = this.responseRequest(link, requestUrl).url.href;
      const templateCacheKey = this.collectionTemplateCacheKey(link, requestUrl);
      const intentId = ++this.intentId;

      if (this.navigationCacheKey !== requestedCacheKey) {
        this.navigationController?.abort();
        if (this.navigationCacheKey) this.deleteCachedResponse(this.navigationCacheKey);
        this.navigationController = null;
        this.navigationCacheKey = null;
      }

      const promotedCacheKey = this.prefetchControllers.has(requestedCacheKey)
        ? requestedCacheKey
        : templateCacheKey;
      const promotedPrefetchController = this.prefetchControllers.get(promotedCacheKey);
      if (promotedPrefetchController) {
        this.prefetchControllers.delete(promotedCacheKey);
        this.navigationController = promotedPrefetchController;
        this.navigationCacheKey = promotedCacheKey;
      }

      this.cancelAnimations();
      this.stopGlide();
      this.setRequestedLink(link);
      if (link) this.scrollLinkIntoView(link);

      if (requestedRouteKey === this.dataset.collectionRouteKey && !forceRequest) {
        this.setActiveLink(requestedRouteKey);
        this.setState('ready');
        this.announce('');
        return;
      }

      this.cacheCurrentPageSnapshot();

      this.setState('loading');
      if (!silent) this.announce(this.dataset.collectionLoadingTemplate, requestedTitle);

      let outgoingAnimations = [];
      let committed = false;
      let committedTitle = '';
      let preparedData = null;
      let preparedOutgoingPromise = null;
      const glidePromise = glideToResults ? this.glideToResults() : Promise.resolve(null);
      if (requirePreparedCommit || glideToResults) {
        this.setState('outgoing');
        preparedOutgoingPromise = glidePromise.then((glide) =>
          intentId === this.intentId
            ? this.animate('out', this.cards, { landing: glide?.target ?? null })
            : []
        );
      }

      try {
        const responseData = await this.readSection(link, 'navigation', requestUrl);
        if (intentId !== this.intentId || !this.isConnected) return;
        const data = this.cloneResponseData(responseData);
        if (typeof prepareData === 'function') {
          preparedData = prepareData(data);
        }
        if (requirePreparedCommit && !preparedData?.commitUrl) {
          this.cancelAnimations();
          const canceledAnimations = await preparedOutgoingPromise;
          this.clearAnimations(canceledAnimations);
          if (intentId === this.intentId && this.isConnected) this.setState('ready');
          return;
        }
        const instantPreparedTransition =
          requirePreparedCommit && Boolean(preparedData?.commitUrl);

        const stylesheetPromise = Promise.all([
          this.loadStylesheets(data),
          loadOwnerStylesheets(data.ownerStylesheetHrefs ?? []),
        ]);
        await stylesheetPromise;
        if (intentId !== this.intentId || !this.isConnected) return;

        const glide = await glidePromise;
        if (preparedOutgoingPromise) {
          outgoingAnimations = await preparedOutgoingPromise;
          if (intentId !== this.intentId || !this.isConnected) {
            this.clearAnimations(outgoingAnimations);
            return;
          }
        } else {
          this.setState('outgoing');
          outgoingAnimations = await this.animate('out');
          if (intentId !== this.intentId || !this.isConnected) {
            this.clearAnimations(outgoingAnimations);
            return;
          }
        }

        preparedData?.beforeCommit?.();
        const navigation = this.commit(
          data,
          link,
          historyMode,
          preparedData?.restoreFocus || restoreFocus,
          preparedData?.commitUrl || requestUrl,
          preserveFilterDialog,
          resultsOnly
        );
        committed = true;
        committedTitle = data.title;
        preparedData?.afterCommit?.(navigation);
        this.loadProductListScript(data.productListScript);
        this.clearAnimations(outgoingAnimations);
        outgoingAnimations = [];
        this.dispatchVirtualPageView(data, navigation);
        this.dispatchStandardViews(data, navigation);

        this.setState('incoming');
        const incomingAnimations = await this.animate(
          'in',
          instantPreparedTransition ? this.cards.filter((card) => !card.hidden) : this.cards,
          { landing: glide?.target ?? null }
        );
        this.clearAnimations(incomingAnimations);
        if (intentId !== this.intentId || !this.isConnected) return;

        this.setState('ready');
        if (preparedData?.afterTransition) {
          preparedData.afterTransition();
        } else {
          this.announce(this.dataset.collectionSuccessTemplate, data.title);
        }
        return {
          ...navigation,
          preparedData,
        };
      } catch (error) {
        if (preparedOutgoingPromise) this.cancelAnimations();
        this.clearAnimations(outgoingAnimations);
        if (intentId !== this.intentId || !this.isConnected || error.name === 'AbortError') return;

        if (committed) {
          this.cancelAnimations();
          this.setState('ready');
          this.announce(this.dataset.collectionSuccessTemplate, committedTitle);
          return;
        }

        this.setState('error');
        this.announce(this.dataset.collectionErrorTemplate, requestedTitle);
        const resolvedFallbackUrl =
          typeof fallbackUrl === 'function' ? fallbackUrl() : fallbackUrl;
        window.setTimeout(
          () => window.location.assign(resolvedFallbackUrl || link.href),
          0
        );
      }
    }

    commit(
      data,
      link,
      historyMode,
      restoreFocus,
      requestUrl = null,
      preserveFilterDialog = false,
      resultsOnly = false
    ) {
      const previousPageUrl = this.lastCommittedPageUrl || document.referrer || '';
      const retainedChangeView = this.dataset.changeView === 'alternate' ? 'alternate' : 'primary';
      const currentHeader = this.querySelector('.g-collection__header');
      const currentIdentity = this.querySelector('[data-collection-ref="identity"]');
      const nextIdentity = data.identity.cloneNode(true);
      const nextResults = data.results.cloneNode(true);
      const replaceHeaderStructure = Boolean(
        currentHeader &&
        currentIdentity &&
        currentHeader.contains(currentIdentity) !== data.identityInHeader
      );
      const currentFilterOwner = this.querySelector('[data-collection-filter-owner]');
      const currentFilterControl = this.querySelector('[data-collection-filter-control]');
      const currentFilterDialog =
        currentFilterOwner?.querySelector('collection-filter-drawer dialog');
      const filterDialogWasOpen = Boolean(preserveFilterDialog && currentFilterDialog?.open);
      const adoptDestinationFilters = Boolean(
        !preserveFilterDialog &&
        !resultsOnly &&
        data.routeKey !== this.dataset.collectionRouteKey
      );

      if (!replaceHeaderStructure && !resultsOnly) {
        if (currentFilterControl && data.filterControl) {
          currentFilterControl.replaceWith(data.filterControl.cloneNode(true));
        } else if (!currentFilterControl && data.filterControl && this.actions) {
          this.actions.append(data.filterControl.cloneNode(true));
        } else if (currentFilterControl && !data.filterControl) {
          currentFilterControl.remove();
        }
      }

      if (currentFilterOwner && data.filterOwner && adoptDestinationFilters) {
        currentFilterOwner
          .querySelector('collection-filter-drawer')
          ?.close({ restoreFocus: false });
        currentFilterOwner.replaceWith(data.filterOwner.cloneNode(true));
      } else if (currentFilterOwner && data.filterOwner) {
        const currentForm = currentFilterOwner.querySelector('[data-collection-ref="filter-form"]');
        const nextForm = data.filterOwner.querySelector('[data-collection-ref="filter-form"]');
        const currentTitle = currentFilterOwner.querySelector('[data-collection-filter-title]');
        const nextTitle = data.filterOwner.querySelector('[data-collection-filter-title]');
        const currentDrawer = currentFilterOwner.querySelector('collection-filter-drawer');
        const nextDrawer = data.filterOwner.querySelector('collection-filter-drawer');

        if (currentForm && nextForm) {
          this.updateFilterForm(currentForm, nextForm, {
            preserveChecked: filterDialogWasOpen,
          });
        }
        if (currentTitle && nextTitle) currentTitle.textContent = nextTitle.textContent;
        currentFilterOwner.dataset.filterModuleSrc = data.filterOwner.dataset.filterModuleSrc || '';
        if (currentDrawer && nextDrawer) {
          currentDrawer.dataset.targetRouteKey = nextDrawer.dataset.targetRouteKey || '';
          currentDrawer.dataset.targetView = nextDrawer.dataset.targetView || '';
          currentDrawer.dataset.targetTitle = nextDrawer.dataset.targetTitle || '';
        }
      } else if (!currentFilterOwner && data.filterOwner) {
        this.results.before(data.filterOwner.cloneNode(true));
      } else if (currentFilterOwner && !data.filterOwner) {
        currentFilterOwner.querySelector('collection-filter-drawer')?.close({ restoreFocus: false });
        currentFilterOwner.remove();
      }

      if (resultsOnly) {
      } else if (replaceHeaderStructure) {
        const currentIdentityRow = currentIdentity.closest('.g-collection__identity-row');
        const nextHeader = data.header.cloneNode(true);

        currentHeader.replaceWith(nextHeader);
        currentIdentityRow?.remove();
        if (data.identityRow) nextHeader.after(data.identityRow.cloneNode(true));
      } else {
        currentIdentity.replaceWith(nextIdentity);
        currentHeader?.classList.toggle(
          'g-collection__header--filtered',
          data.header.classList.contains('g-collection__header--filtered')
        );
      }
      this.results.replaceWith(nextResults);
      const skipLink = this.querySelector(':scope > s-view-event > .skip-to-content-link');
      if (skipLink && nextResults.id) skipLink.href = `#${nextResults.id}`;
      if (!resultsOnly) {
        this.className = data.className;
        this.dataset.sectionId = data.sectionId;
        this.dataset.collectionView = data.view;
        this.dataset.collectionRouteKey = data.routeKey;
        this.toggleAttribute('data-primary-collection', data.isPrimaryCollection);
        this.toggleAttribute('data-filter-target-self', data.filtersTargetSelf);
        this.applyDynamicStyles(data);
        this.commitSections(data);
      }
      this.refreshReferences();
      if (!resultsOnly) attachRailIndicator(this.tabsRail, () => this.arcTarget());
      this.loadFilterModule();
      this.filterOwner
        ?.querySelector('collection-filter-drawer')
        ?.onCollectionCommit?.();

      let activeLink = null;
      if (!resultsOnly) {
        if (data.handle) this.dataset.activeHandle = data.handle;
        else delete this.dataset.activeHandle;
        this.dataset.activeTitle = data.title;
        this.setAttribute('view-event-payload', data.viewEventPayload);
        const viewEvent = this.querySelector(':scope > s-view-event');
        if (viewEvent) {
          if (data.viewEventPayload) viewEvent.setAttribute('view-event-payload', data.viewEventPayload);
          else viewEvent.removeAttribute('view-event-payload');
        }
        activeLink = this.setActiveLink(data.routeKey);
        this.scrollLinkIntoView(activeLink);
      }
      if (restoreFocus?.type === 'filter-option') {
        const matchingInput = Array.from(
          this.filterOwner?.querySelectorAll(
            '[data-collection-ref="filter-form"] input[type="checkbox"]'
          ) || []
        ).find(
          (input) =>
            input.name === restoreFocus.name &&
            input.value === restoreFocus.value &&
            !input.disabled
        );
        (
          matchingInput ||
          this.filterOwner?.querySelector('[data-collection-filter-close]')
        )?.focus({ preventScroll: true });
      } else if (restoreFocus?.type === 'filter-reset') {
        const reset = this.filterOwner?.querySelector('[data-collection-filter-reset]');
        (
          (reset?.hidden ? null : reset) ||
          this.filterOwner?.querySelector('[data-collection-filter-close]')
        )?.focus({ preventScroll: true });
      } else if (restoreFocus === 'filter') {
        this.filterButton?.focus({ preventScroll: true });
      } else if (restoreFocus) {
        activeLink?.focus({ preventScroll: true });
      }

      if (this.actions) {
        this.actions.hidden = data.actionsHidden;
      }
      if (this.changeViewButton) {
        this.changeViewButton.disabled = data.actionsHidden;
        this.changeViewButton.setAttribute('aria-disabled', String(data.actionsHidden));
        if (!data.actionsHidden) this.changeViewButton.removeAttribute('aria-disabled');
      }
      this.dataset.changeView = retainedChangeView;
      this.changeViewButton?.setAttribute(
        'aria-pressed',
        String(retainedChangeView === 'alternate')
      );

      if (this.filterButton) {
        const filterAvailable = Boolean(data.filterOwner);
        this.filterButton.hidden = !filterAvailable;
        this.filterButton.disabled = !filterAvailable;
        this.filterButton.setAttribute('aria-disabled', String(!filterAvailable));
        this.filterButton.setAttribute('aria-expanded', String(filterDialogWasOpen));
        const controlledDialogId =
          this.filterOwner?.querySelector('dialog')?.id || data.filterButtonControls;
        if (controlledDialogId) {
          this.filterButton.setAttribute('aria-controls', controlledDialogId);
        } else {
          this.filterButton.removeAttribute('aria-controls');
        }
      }
      if (filterDialogWasOpen) {
        document.documentElement.setAttribute('data-collection-filter-open', 'true');
      }
      this.scheduleArc();

      const target = new URL(requestUrl || link.href, window.location.href);
      const path = `${target.pathname}${target.search}${target.hash}`;
      const state = {
        ...(history.state || {}),
        dieuxCollectionRouteKey: data.routeKey,
        dieuxCollectionView: data.view,
        dieuxCollectionSectionId: data.sectionId,
        dieuxCollectionUrl: path,
        dieuxCollectionTitle: data.title,
      };

      if (historyMode === 'push') {
        history.pushState(state, '', path);
      } else if (historyMode === 'replace') {
        history.replaceState(state, '', path);
      }
      if (!resultsOnly) {
        this.commitMetadata(data);
        const main = document.getElementById('MainContent');
        if (main) main.dataset.template = data.pageTemplate;
      }
      const committedUrl = new URL(window.location.href);
      this.lastCommittedPageUrl = committedUrl.href;
      return { committedUrl, previousPageUrl };
    }

    commitMetadata(data) {
      this.dataset.pageTitle = data.pageTitle;
      this.dataset.pageTemplate = data.pageTemplate;
      this.dataset.documentTitle = data.documentTitle;
      this.dataset.canonicalUrl = data.canonicalUrl;
      this.dataset.ogDescription = data.ogDescription;
      this.toggleAttribute('data-has-page-description', data.hasPageDescription);

      if (data.hasPageDescription) this.dataset.pageDescription = data.pageDescription;
      else delete this.dataset.pageDescription;

      if (data.pageImageUrl) {
        this.dataset.pageImageUrl = data.pageImageUrl;
        this.dataset.pageImageWidth = data.pageImageWidth;
        this.dataset.pageImageHeight = data.pageImageHeight;
      } else {
        delete this.dataset.pageImageUrl;
        delete this.dataset.pageImageWidth;
        delete this.dataset.pageImageHeight;
      }

      document.title = data.documentTitle;
      const canonical = document.head.querySelector('link[rel="canonical"]');
      if (canonical) canonical.href = data.canonicalUrl;

      this.setMeta('meta[property="og:url"]', 'property', 'og:url', data.canonicalUrl);
      this.setMeta('meta[property="og:title"]', 'property', 'og:title', data.pageTitle);
      this.setMeta(
        'meta[property="og:description"]',
        'property',
        'og:description',
        data.ogDescription
      );
      this.setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', data.pageTitle);
      this.setMeta(
        'meta[name="twitter:description"]',
        'name',
        'twitter:description',
        data.ogDescription
      );
      this.setMeta(
        'meta[name="description"]',
        'name',
        'description',
        data.pageDescription,
        !data.hasPageDescription
      );
      this.commitPageImage(data);
    }

    setMeta(selector, identifyingAttribute, identifyingValue, content, remove = false) {
      let element = document.head.querySelector(selector);
      if (remove) {
        element?.remove();
        return;
      }

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(identifyingAttribute, identifyingValue);
        document.head.append(element);
      }
      element.setAttribute('content', content || '');
    }

    commitPageImage(data) {
      const imageSelectors = [
        'meta[property="og:image"]',
        'meta[property="og:image:secure_url"]',
        'meta[property="og:image:width"]',
        'meta[property="og:image:height"]',
      ];

      if (!data.pageImageUrl) {
        imageSelectors.forEach((selector) => {
          document.head.querySelectorAll(selector).forEach((element) => element.remove());
        });
        return;
      }

      const protocolRelative = data.pageImageUrl.startsWith('//');
      const insecureUrl = protocolRelative ? `http:${data.pageImageUrl}` : data.pageImageUrl;
      const secureUrl = protocolRelative ? `https:${data.pageImageUrl}` : data.pageImageUrl;
      this.setMeta('meta[property="og:image"]', 'property', 'og:image', insecureUrl);
      this.setMeta(
        'meta[property="og:image:secure_url"]',
        'property',
        'og:image:secure_url',
        secureUrl
      );
      this.setMeta(
        'meta[property="og:image:width"]',
        'property',
        'og:image:width',
        data.pageImageWidth
      );
      this.setMeta(
        'meta[property="og:image:height"]',
        'property',
        'og:image:height',
        data.pageImageHeight
      );
    }

    dispatchStandardViews(data, navigation) {
      let collectionPayload = null;
      try {
        collectionPayload = JSON.parse(data.viewEventPayload);
      } catch {}

      const { committedUrl } = navigation;
      let previousUrl = null;
      try {
        if (navigation.previousPageUrl) {
          previousUrl = new URL(navigation.previousPageUrl, window.location.href);
        }
      } catch {}

      const samePath = Boolean(previousUrl) && previousUrl.pathname === committedUrl.pathname;
      const filtersChanged = !samePath
        || filterSignature(previousUrl) !== filterSignature(committedUrl);

      const pagePayload = {
        page: {
          template: data.pageTemplate,
          title: data.documentTitle,
          url: committedUrl.href,
        },
      };

      standardEventsPromise.then((standardEvents) => {
        if (!standardEvents || !this.isConnected) return;

        try {
          if (!samePath) {
            document.dispatchEvent(new standardEvents.PageViewEvent(pagePayload));
            if (data.pageTemplate === 'collection' && collectionPayload) {
              this.dispatchEvent(new standardEvents.CollectionViewEvent(collectionPayload));
            }
            return;
          }

          if (
            !filtersChanged ||
            typeof standardEvents.CollectionUpdateEvent !== 'function' ||
            !collectionPayload?.collection
          ) {
            return;
          }

          let resolveUpdate;
          const promise = new Promise((resolve) => {
            resolveUpdate = resolve;
          });
          const updatePayload = {
            collection: collectionPayload.collection,
            productFilters: productFiltersFromParams(committedUrl.searchParams),
            promise,
          };
          const sortKey = committedUrl.searchParams.get('sort_by');
          if (sortKey) updatePayload.sortKey = sortKey;
          this.dispatchEvent(new standardEvents.CollectionUpdateEvent(updatePayload));
          resolveUpdate({ productsCount: collectionPayload.collection.productsCount ?? 0 });
        } catch {
          // Analytics interoperability must never block collection navigation.
        }
      });
    }

    dispatchVirtualPageView(data, navigation) {
      try {
        document.dispatchEvent(
          new CustomEvent('dieux:virtual-page-view', {
            detail: {
              dieuxVirtualPageView: true,
              page: {
                template: data.pageTemplate,
                title: data.documentTitle,
                url: navigation.committedUrl.href,
              },
              pageReferrer: navigation.previousPageUrl,
            },
          })
        );
      } catch {
      }
    }

    /**
     * Which link the shared rail indicator belongs under right now.
     *
     * @returns {HTMLElement|null} The link, or `null` when there is nothing to mark.
     */
    arcTarget() {
      const requestedTarget = this.querySelector(`${linkSelector}[data-dieux-tabs-requested]`);
      const currentTarget = this.querySelector(`${linkSelector}[aria-current="page"]`);
      if (this.isArcLocked()) return requestedTarget || currentTarget;

      return this.interactionArcTarget() || requestedTarget || currentTarget;
    }

    scheduleArc = () => {
      this.tabsRail?.scheduleIndicator?.();
    };

    scrollLinkIntoView(link, options = {}) {
      revealInRail(this.tabsRail, link, options);
    }

    /**
     * The scroll offset at which the results sit directly under the pinned header and collection nav.
     *
     * @returns {number|null} Offset in pixels, or `null` when there are no results to land on.
     */
    landingOffset() {
      const results = this.results;
      if (!this.isConnected || !results) return null;

      const header = this.querySelector('.g-collection__header');
      const headerStyle = header ? window.getComputedStyle(header) : null;
      const pinnedBottom =
        headerStyle?.position === 'sticky'
          ? (Number.parseFloat(headerStyle.top) || 0) + header.getBoundingClientRect().height
          : window.DieuxFrame.resolveCustomProperty(this, '--header-height');
      return Math.max(
        0,
        Math.ceil(results.getBoundingClientRect().top + window.scrollY - pinnedBottom)
      );
    }

    /**
     * Starts easing the page up to the results when the visitor is below them.
     *
     * @returns {Promise<{target: number}|null>} The landing offset, or `null` when nothing moves.
     */
    glideToResults() {
      return new Promise((resolve) => {
        window.DieuxFrame.read(() => {
          const target = this.landingOffset();
          if (target === null || window.scrollY <= target + 1) {
            resolve(null);
            return;
          }

          const anchorScroll = window.DieuxAnchorScroll;
          if (!anchorScroll?.toOffset) {
            window.DieuxFrame.write(() => window.scrollTo({ top: target, behavior: 'instant' }));
            resolve({ target });
            return;
          }

          const arrival = anchorScroll.toOffset(() => this.landingOffset());
          this.activeGlide = arrival;
          arrival.then(() => {
            if (this.activeGlide === arrival) this.activeGlide = null;
          });
          resolve({ target });
        });
      });
    }

    stopGlide() {
      if (!this.activeGlide) return;
      this.activeGlide = null;
      window.DieuxAnchorScroll?.cancel();
    }
  }

  if (!customElements.get('collection-component')) {
    customElements.define('collection-component', CollectionComponent);
  }
})();
